import { createContext, useContext } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { IWedding_RSVP, IWedding_Website } from '@bridebook/models/source/models/Weddings.types';
import { IWeddingEvent } from '@bridebook/models/source/models/Weddings/Events.types';
import { IGuest_Invitation } from '@bridebook/models/source/models/Weddings/Guests.types';
import { GetRsvpDataResponse } from '@bridebook/web/pages/api/for/website/[websiteId]/rsvp/guest/get';
import {
  GetWeddingWebsiteDataResponseSuccess,
  IWeddingEventSupplier,
} from '@bridebook/web/pages/api/for/website/[websiteId]';
import { SaveGuestRsvpResponse } from '@bridebook/web/pages/api/for/website/[websiteId]/rsvp/guest/save';
import {
  INameFormFields,
  TGuestRSVPStep,
  TGuestRsvpSubmissionStatus,
} from '@bridebook/web/lib/rsvp/types';
import noop from 'lodash/noop';

// Define enriched event type
export interface IEnrichedEvent {
  data: IWeddingEvent;
  supplier: IWeddingEventSupplier | undefined;
  eventId: string;
}

// Core RSVP state
export interface IRsvpStateContext {
  websiteId: string;
  isPreview: boolean;
  isRsvpOpen: boolean;
  rsvpSubmissionStatus: TGuestRsvpSubmissionStatus;
  setRsvpSubmissionStatus: (status: TGuestRsvpSubmissionStatus) => void;
}

// Navigation state
export interface IRsvpNavigationContext {
  step: TGuestRSVPStep;
  setStep: (step: TGuestRSVPStep) => void;
  currentQuestionIndex: number;
  setCurrentQuestionIndex: (index: number) => void;
  onClose?: () => void;
}

// Form state
export interface IRsvpFormsContext {
  nameForm: UseFormReturn<INameFormFields>;
  eventsForm: UseFormReturn;
  questionsForm: UseFormReturn;
  handleSubmit: () => Promise<{ response: SaveGuestRsvpResponse }> | (() => Promise<void>);
}

// Data/API state
export interface IRsvpDataContext {
  isLoading: boolean;
  error: string | null;
  guestNotFound: boolean;
  guestNoEvents: boolean;
  guestInvitation: IGuest_Invitation | undefined;
  weddingRsvp: IWedding_RSVP | undefined;
  weddingWebsite: GetWeddingWebsiteDataResponseSuccess;
  theme: IWedding_Website['config']['theme'];
  enrichedEvents: IEnrichedEvent[];
  relatedGuests: GetRsvpDataResponse['relatedGuests'];
}

// Analytics state
export interface IRsvpAnalyticsContext {
  trackGuestRsvpStarted: () => void;
  trackGuestRsvpSent: () => void;
}

export const NOOP_RSVP_ANALYTICS: IRsvpAnalyticsContext = {
  trackGuestRsvpStarted: noop,
  trackGuestRsvpSent: noop,
};

// New focused contexts
export const RsvpStateContext = createContext<IRsvpStateContext | undefined>(undefined);
export const RsvpNavigationContext = createContext<IRsvpNavigationContext | undefined>(undefined);
export const RsvpFormsContext = createContext<IRsvpFormsContext | undefined>(undefined);
export const RsvpDataContext = createContext<IRsvpDataContext | undefined>(undefined);
export const RsvpAnalyticsContext = createContext<IRsvpAnalyticsContext | undefined>(undefined);

interface IRsvpStateProviderProps extends React.PropsWithChildren {
  value: IRsvpStateContext;
}

interface IRsvpNavigationProviderProps extends React.PropsWithChildren {
  value: IRsvpNavigationContext;
}

interface IRsvpFormsProviderProps extends React.PropsWithChildren {
  value: IRsvpFormsContext;
}

interface IRsvpDataProviderProps extends React.PropsWithChildren {
  value: IRsvpDataContext;
}

interface IRsvpAnalyticsProviderProps extends React.PropsWithChildren {
  value: IRsvpAnalyticsContext;
}

// New focused providers
export const RsvpStateProvider: React.FC<IRsvpStateProviderProps> = ({ value, children }) => (
  <RsvpStateContext.Provider value={value}>{children}</RsvpStateContext.Provider>
);

export const RsvpNavigationProvider: React.FC<IRsvpNavigationProviderProps> = ({
  value,
  children,
}) => <RsvpNavigationContext.Provider value={value}>{children}</RsvpNavigationContext.Provider>;

export const RsvpFormsProvider: React.FC<IRsvpFormsProviderProps> = ({ value, children }) => (
  <RsvpFormsContext.Provider value={value}>{children}</RsvpFormsContext.Provider>
);

export const RsvpDataProvider: React.FC<IRsvpDataProviderProps> = ({ value, children }) => (
  <RsvpDataContext.Provider value={value}>{children}</RsvpDataContext.Provider>
);

export const RsvpAnalyticsProvider: React.FC<IRsvpAnalyticsProviderProps> = ({
  value,
  children,
}) => <RsvpAnalyticsContext.Provider value={value}>{children}</RsvpAnalyticsContext.Provider>;

// New focused hooks
export const useRsvpState = (): IRsvpStateContext => {
  const context = useContext(RsvpStateContext);
  if (!context) {
    throw new Error('useRsvpState must be used within a RsvpStateProvider');
  }
  return context;
};

export const useRsvpNavigation = (): IRsvpNavigationContext => {
  const context = useContext(RsvpNavigationContext);
  if (!context) {
    throw new Error('useRsvpNavigation must be used within a RsvpNavigationProvider');
  }
  return context;
};

export const useRsvpForms = (): IRsvpFormsContext => {
  const context = useContext(RsvpFormsContext);
  if (!context) {
    throw new Error('useRsvpForms must be used within a RsvpFormsProvider');
  }
  return context;
};

export const useRsvpData = (): IRsvpDataContext => {
  const context = useContext(RsvpDataContext);
  if (!context) {
    throw new Error('useRsvpData must be used within a RsvpDataProvider');
  }
  return context;
};

export const useRsvpAnalytics = (): IRsvpAnalyticsContext => {
  const context = useContext(RsvpAnalyticsContext);
  if (!context) {
    throw new Error('useRsvpAnalytics must be used within a RsvpAnalyticsProvider');
  }
  return context;
};
